import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { S7302EpcraComplianceSnapshot } from "@/lib/s7302EpcraCompliance";
import { cn } from "@/lib/utils";

function levelStyles(level: S7302EpcraComplianceSnapshot["worst_level"]): string {
  if (level === "at_or_over") {
    return "border-rose-300 bg-rose-50 text-rose-950";
  }
  if (level === "approaching") {
    return "border-amber-300 bg-amber-50 text-amber-950";
  }
  return "border-slate-200 bg-slate-50 text-slate-800";
}

function levelTitle(level: S7302EpcraComplianceSnapshot["worst_level"]): string {
  if (level === "at_or_over") {
    return "S7302 potting — EPCRA reporting threshold reached";
  }
  if (level === "approaching") {
    return "S7302 potting — approaching EPCRA reporting threshold";
  }
  return "S7302 potting — EPCRA inventory within limits";
}

type S7302EpcraComplianceBannerProps = {
  /** When true, show the banner even if all facilities are ok (muted style). */
  showWhenOk?: boolean;
  className?: string;
};

export function S7302EpcraComplianceBanner({ showWhenOk = false, className }: S7302EpcraComplianceBannerProps) {
  const query = useQuery({
    queryKey: ["mrp", "s7302-epcra-compliance"],
    queryFn: () => apiFetch<S7302EpcraComplianceSnapshot>("/api/mrp/s7302-epcra-compliance"),
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.data?.worst_level === "ok" ? 120_000 : 60_000,
  });

  const snapshot = query.data;
  if (!snapshot) {
    return null;
  }
  if (snapshot.worst_level === "ok" && !showWhenOk) {
    return null;
  }

  const facilitiesNeedingAttention = snapshot.facilities.filter(
    (facility) => facility.worst_level !== "ok",
  );

  return (
    <div
      className={cn(
        "rounded-lg border px-4 py-3 text-sm",
        levelStyles(snapshot.worst_level),
        className,
      )}
      role="status"
    >
      <p className="font-semibold">{levelTitle(snapshot.worst_level)}</p>
      <p className="mt-1 text-[13px] leading-relaxed">
        Epic Resins S7302 Part A + Part B on hand per facility (Megaventory physical stock). Federal
        Tier II mixture reporting at 10,000 lb; MDI in Part B at 500 lb (Tier II component) and 5,000
        lb (Section 302). Hub warns at 80% and 100%. MDI estimate uses{" "}
        {(snapshot.mdi_weight_fraction_used * 100).toFixed(0)}% of Part B weight
        {snapshot.mdi_weight_fraction_source === "default" ? " (default — confirm on Epic SDS)" : ""}.
      </p>
      <ul className="mt-2 space-y-1 text-[13px]">
        {snapshot.facilities.map((facility) => {
          const hot = facility.thresholds.filter((threshold) => threshold.level !== "ok");
          return (
            <li key={facility.location_code}>
              <span className="font-medium">{facility.display_name}:</span>{" "}
              {facility.mixture_total_lb.toLocaleString()} lb mixture (
              {facility.part_a_lb.toLocaleString()} lb A, {facility.part_b_lb.toLocaleString()} lb B
              {facility.mdi_lb_estimate > 0
                ? `; ~${facility.mdi_lb_estimate.toLocaleString()} lb MDI est.`
                : ""}
              )
              {hot.length > 0 ? (
                <span>
                  {" "}
                  —{" "}
                  {hot
                    .map(
                      (threshold) =>
                        `${threshold.label} ${threshold.level === "at_or_over" ? "at/over" : "approaching"} (${threshold.current_lb.toLocaleString()} / ${threshold.limit_lb.toLocaleString()} lb)`,
                    )
                    .join("; ")}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {facilitiesNeedingAttention.length === 0 && showWhenOk ? (
        <p className="mt-2 text-[13px]">SGS is below 80% of every tracked threshold.</p>
      ) : null}
    </div>
  );
}

export function isS7302PottingSku(sku: string | null | undefined): boolean {
  const normalized = (sku ?? "").trim().toUpperCase();
  return (
    normalized.startsWith("S7302-A") ||
    normalized.startsWith("S7302-B") ||
    normalized.startsWith("S7302B")
  );
}
